import React, { useMemo } from "react";
import Dot from "./Dot";

// Constants
const HOVER_BOX_WIDTH = 250;
const POSITION_OFFSET = 30;

// Utility functions
const calculatePosition = (mousePosition, isRtl) => {
  const position = {};
  
  // Vertical positioning
  if (mousePosition.y < window.innerHeight / 2) {
    position.top = mousePosition.y;
  } else {
    position.bottom = window.innerHeight - mousePosition.y;
  }
  
  // Horizontal positioning
  if (!isRtl) {
    position.left = mousePosition.x + POSITION_OFFSET;
  } else {
    position.left = mousePosition.x - HOVER_BOX_WIDTH - POSITION_OFFSET;
  }
  
  return position;
};

const getApartmentStatus = (apartment, statuses) => {
  return apartment && statuses.find((status) => 
    status && status.status_name === apartment.available
  );
};

// Sub-components
const StatusHeader = ({ status, isRtl }) => (
  <div
    className="header"
    style={{
      display: "flex",
      alignItems: "start",
    }}
  >
    {status && (
      <Dot
        cx={3}
        cy={3}
        radius={3}
        fill={status.status_color}
        style={{
          width: ".5rem",
          height: ".5rem",
          marginTop: "5px",
        }}
      />
    )}
    <span
      style={{
        marginRight: ".5rem",
        marginLeft: ".5rem",
      }}
    >
      {status?.status_name}
    </span>
  </div>
);

const ApartmentDetails = ({ apartmentBlockFieldsOrder, apartment }) => (
  <div className="detailsList">
    <ul>
      {apartmentBlockFieldsOrder.map((detail, index) => {
        const fieldValue = apartment.display_fields.find(
          (field) => field.field === detail.field
        )?.value;
        if (!fieldValue) return null;
        return (
          <li key={index}>
            <div className="d-flex mb-1">
              <span>
                {detail.title} {fieldValue}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  </div>
);

const FloorDisplay = ({ apartment, isRtl }) => (
  <div 
    className="floor"
    style={{
      position: 'absolute',
      top: '5px',
      left: !isRtl ? 'auto' : '1.5rem',
      right: !isRtl ? '1.5rem' : 'auto',
    }}
  >
    <span
      style={{
        marginRight: ".5rem",
        marginLeft: ".5rem",
      }}
    >
      {isRtl ? "קומה" : "Floor"}
    </span>
    <div
      style={{
        fontSize: 36,
      }}
    >
      {apartment.floor_name}
    </div>
  </div>
);

const TourButton = ({ apartment, goToApartmentPage, colors, isRtl }) => (
  <div
    className="roundButton"
    onClick={() => goToApartmentPage(apartment)}
    style={{
      border: `1px solid ${colors.primary}`,
    }}
  >
    <span>{isRtl ? "סיור" : "Tour"}</span>
  </div>
);

const HoverApp = ({
  apartment,
  mousePosition,
  goToApartmentPage,
  isRtl,
  colors,
  statuses,
  apartmentBlockFieldsOrder,
}) => {
  const status = useMemo(() => 
    getApartmentStatus(apartment, statuses), 
    [apartment, statuses]
  );

  const containerStyle = useMemo(() => {
    const baseStyle = {
      position: "absolute",
      direction: isRtl ? "rtl" : "ltr",
      backgroundColor: "rgba(0, 0, 0, 0.6)",
    };

    if (apartment && mousePosition) {
      return {
        ...baseStyle,
        ...calculatePosition(mousePosition, isRtl),
      };
    }

    return baseStyle;
  }, [apartment, mousePosition, isRtl]);

  const ContentComponent = () => (
    <>
      <StatusHeader status={status} isRtl={isRtl} />
      
      <div
        className="mt-1"
        style={{
          fontSize: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <ApartmentDetails 
          apartmentBlockFieldsOrder={apartmentBlockFieldsOrder} 
          apartment={apartment} 
        />
        
        <div className="">
          <FloorDisplay apartment={apartment} isRtl={isRtl} />
          <TourButton 
            apartment={apartment} 
            goToApartmentPage={goToApartmentPage} 
            colors={colors} 
            isRtl={isRtl} 
          />
        </div>
      </div>
    </>
  );

  return (
    <div
      className={`apartmentBox rounded shadowed ${apartment ? "active" : ""}`}
      style={containerStyle}
    >
      {apartment && <ContentComponent />}
    </div>
  );
};

export default HoverApp;
